import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const OBJECTIVES = ['LINK_CLICKS', 'POST_ENGAGEMENT', 'PAGE_LIKES'];

const LOCATIONS = [
  {
    label: 'Worldwide',
    value: 'worldwide',
  },
  { label: 'USA', value: 'US' },
  { label: 'Europe', value: 'europe' },
  { label: 'North America', value: 'north_america' },
  { label: 'South America', value: 'south_america' },
  { label: 'Central America', value: 'central_america' },
];

interface FormDataT {
  locations: string[];
  daily_budget: string;
  bid_amount: string;
  start_date: string;
  end_date: string;
  objective: string;
}

interface Props {
  onClose: () => void;
  post: EditorialFacebookPostResource;
}

const NewAdForm: FC<Props> = ({ onClose, post }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      locations: [],
      daily_budget: '',
      bid_amount: '',
      start_date: '',
      end_date: '',
      objective: '',
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async ({ locations, ...rest }) => {
      try {
        const data = {
          post_id: post.id,
          ...rest,
          ...(!!locations.length && {
            geo_locations: {
              ...(locations.includes('US') && { countries: ['US'] }),
              country_groups: locations.filter((l) => l !== 'US'),
            },
          }),
        };

        const { error } = await api.post<typeof data, { error: null | string }>(
          `/v1/editorial/facebook-post-ads`,
          data,
        );

        if (error) {
          return alerts.error('Something went wrong!', error);
        }

        await queryClient.invalidateQueries(
          [
            'fb_post_ads',
            {
              postId: post.id,
            },
          ],
          {
            exact: false,
          },
        );

        onClose();

        alerts.success('A new ad has been created successfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal zIndex={50}>
      <SidePanel heading={`New Ad`} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                label="Objective"
                type="select"
                control={control}
                options={OBJECTIVES.map((o) => ({ label: o, value: o }))}
                name="objective"
              />
              <FormField<FormDataT>
                label="Locations"
                type="multiselect"
                control={control}
                options={LOCATIONS}
                name="locations"
              />
              <FormField<FormDataT>
                label="Daily Budget"
                type="text"
                subLabel="(USD)"
                control={control}
                name="daily_budget"
                placeholder="Add daily budget"
              />
              <FormField<FormDataT>
                label="Bid Amount"
                type="text"
                subLabel="(USD)"
                control={control}
                name="bid_amount"
                placeholder="Add bid amount"
              />
              <FormField<FormDataT>
                label="Start Date"
                control={control}
                type="datepicker"
                name="start_date"
              />
              <FormField<FormDataT>
                label="End Date"
                control={control}
                type="datepicker"
                name="end_date"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Create
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default NewAdForm;
